import { useId, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

export interface RadioCardOption<T extends string> {
  value: T;
  label: string;
  description?: ReactNode;
  icon?: ReactNode;
  disabled?: boolean;
}

interface RadioGroupProps<T extends string> {
  name: string;
  legend: string;
  value: T;
  onChange: (value: T) => void;
  options: readonly RadioCardOption<T>[];
  columns?: 1 | 2;
}

/** Native radio inputs rendered as selectable cards. Arrow-key navigation is provided by the browser. */
export function RadioGroup<T extends string>({ name, legend, value, onChange, options, columns = 2 }: RadioGroupProps<T>) {
  const groupId = useId();
  return (
    <fieldset>
      <legend className="mb-2 text-sm font-medium text-ink">{legend}</legend>
      <div className={cn('grid gap-3', columns === 2 && 'sm:grid-cols-2')}>
        {options.map((opt) => {
          const id = `${groupId}-${opt.value}`;
          const selected = opt.value === value;
          return (
            <label
              key={opt.value}
              htmlFor={id}
              className={cn(
                'flex items-start gap-3 rounded-lg border p-4 transition-colors has-[:focus-visible]:shadow-focus',
                opt.disabled ? 'cursor-not-allowed border-line opacity-60' : 'cursor-pointer',
                !opt.disabled && (selected ? 'border-primary bg-primary-soft/60' : 'border-line hover:border-line-strong hover:bg-surface-muted'),
              )}
            >
              <input
                id={id}
                type="radio"
                name={name}
                value={opt.value}
                checked={selected}
                disabled={opt.disabled}
                onChange={() => onChange(opt.value)}
                className="mt-0.5 h-4 w-4 shrink-0 accent-[rgb(var(--color-primary))] focus:outline-none"
              />
              <span className="min-w-0">
                <span className="flex items-center gap-2 text-sm font-medium text-ink">
                  {opt.icon}
                  {opt.label}
                </span>
                {opt.description && <span className="mt-0.5 block text-[13px] text-ink-secondary">{opt.description}</span>}
              </span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}
